/**
 * KnowledgeGatePanel — the technician's half of the Adaptive Knowledge Gate (MASTER_FLOW §4.1).
 *
 * §4.1 splits a Digital Employee's work in two: the normal path (approved Expert Pack -> plan ->
 * policy/approval -> bounded execution) and the mismatch path (something observed contradicts the
 * pack -> PAUSE BEFORE MUTATION -> targeted official vendor lookup -> reconcile -> continue or
 * escalate). The backend half of the mismatch path shipped first: the deterministic evaluator, the
 * append-only decision trail, and the refusal in the approve event that stops an already-approved
 * mutation. This panel is the other half, and without it the mismatch path is not a pilot workflow —
 * a technician who hits the refusal had no way to see why, and no way to record the evidence that
 * clears it, short of a raw HTTP call with a bearer token.
 *
 * What it is: two reads of the run's gate state — its newest decision, and the append-only trail that
 * decision sits at the end of — plus the two acts §4.1 names, recording an observed mismatch and
 * recording the targeted vendor research that may reconcile it. Those are the only writes, they hit
 * only the existing `knowledge-gate` endpoints, and neither can execute, approve, grant a scope or
 * change customer state. Clearing a mismatch only re-permits an action a human already approved, which
 * is why the research write needs `agents_approve` on the server: the panel offers it only to a user
 * who holds that permission, and otherwise says who to hand it to.
 *
 * Truthfulness rules, each pinned by a test:
 *   - the server's answer is the only answer. Every write re-renders from the response body; nothing
 *     here computes "unblocked" locally, and a payload that does not carry a boolean permits_mutation
 *     reads as unverifiable, never as clear;
 *   - a 404 is "nothing was ever observed against this run" (the normal path), not "approved" —
 *     the panel says so in those words, because the executor, not this read, permits execution;
 *   - an unreadable gate state is stated as unreadable and as blocking, mirroring manager_agent,
 *     which refuses the action in that same state;
 *   - a trigger the server recorded but this build cannot name is shown verbatim, never dropped;
 *   - the observation form refuses to submit with zero triggers, because an empty observation
 *     evaluates to PROCEED and would silently UNBLOCK a run rather than restrict it;
 *   - the research form offers no date field: the retrieval time is stamped by the server so it
 *     cannot be backdated;
 *   - the trail never reads as the whole record when it could not be read, and never decides the gate
 *     state — see `DecisionTrail`.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { knowledgeGateApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import {
  GATE_DECISIONS,
  GATE_STATE,
  GATE_TRAIL_STATUS,
  GATE_TRIGGERS,
  OBSERVATION_CONTEXT_FIELDS,
  RESEARCH_AUTHORITIES,
  classifyGateQuery,
  classifyGateTrailQuery,
  gateNextStep,
  gateReasonLabel,
  gateTriggerMeta,
  researchAuthorityMeta,
} from "./knowledgeGateVocabulary";

export const knowledgeGateQueryKey = (runId) => ["knowledge-gate", runId];
// A key of its own rather than part of the state read's: the two are separate requests with separate
// lifetimes, and a write's response updates the state without invalidating the trail it just appended
// to — which is why the trail is invalidated explicitly after a write instead (see `onRecorded`).
export const knowledgeGateHistoryQueryKey = (runId) => ["knowledge-gate", runId, "history"];

const STATE_STYLE = {
  [GATE_STATE.BLOCKED]: "border-red-300 bg-red-50",
  [GATE_STATE.CLEARED]: "border-violet-300 bg-violet-50",
  [GATE_STATE.NORMAL_PATH]: "border-gray-200 bg-gray-50",
  [GATE_STATE.UNKNOWN]: "border-amber-300 bg-amber-50",
  [GATE_STATE.LOADING]: "border-gray-200 bg-white",
};

const DECISION_LABEL = {
  [GATE_DECISIONS.PROCEED]: "ניתן להמשיך",
  [GATE_DECISIONS.RESEARCH_REQUIRED]: "נדרש מחקר ממוקד",
  [GATE_DECISIONS.STOP_ESCALATE]: "עצור והסלם",
};

// `testId` is a parameter rather than a constant because the trail below renders one of these per
// recorded decision: a second element with the state block's test id would make "the run's triggers"
// ambiguous to every reader, human or test.
function TriggerList({ triggers, testId = "kg-triggers" }) {
  if (!triggers.length) return null;
  return (
    <ul className="space-y-1" data-testid={testId}>
      {triggers.map((id) => {
        const meta = gateTriggerMeta(id);
        return (
          <li key={id} className="text-xs">
            <span className="font-medium">{meta.label}</span>
            <span className="text-gray-600"> — {meta.explain}</span>
          </li>
        );
      })}
    </ul>
  );
}

function RecordedContext({ context }) {
  const entries = Object.entries(context || {}).filter(([, v]) => v !== null && v !== undefined && v !== "");
  if (!entries.length) return null;
  return (
    <div className="text-xs text-gray-600" data-testid="kg-context">
      <span className="font-medium">ההקשר שנרשם:</span>{" "}
      {entries.map(([k, v]) => `${k}=${v}`).join(", ")}
    </div>
  );
}

/**
 * What the decision was evaluated *against* — as opposed to `RecordedContext`, which is what it saw.
 *
 * §4.1 requires the exact knowledge version a historical run used to stay recoverable, and the
 * backend has persisted it since FULL-0008. Until this block existed it was on the API and in the
 * audit trail and on no screen, so a technician reading a blocked run could not tell which approved
 * Expert Pack revision the mismatch was measured against. That is the one thing they need in order to
 * know what to reconcile it with.
 *
 * Every line is descriptive. It is not a reason for the decision and cannot become one: the gate
 * blocks on `permits_mutation` alone, and nothing here is consulted for the state.
 */
function DecisionBasis({ basis }) {
  if (!basis) return null;
  const { pack, contractId, contractRevision } = basis;
  const contractRecorded = !!(contractId || contractRevision);
  return (
    <div className="text-xs text-gray-700 space-y-0.5" data-testid="kg-basis">
      <p className="font-medium">הבסיס שההכרעה נשענה עליו:</p>
      <p data-testid="kg-basis-pack">
        <span className="font-medium">חבילת המומחה המאושרת:</span>{" "}
        {pack ? (
          <>
            {pack.scopeKey} · מהדורה {pack.revisionSeq} ·{" "}
            <span className="font-mono break-all">{pack.contentHash}</span>
          </>
        ) : (
          "לא נרשמה"
        )}
      </p>
      <p data-testid="kg-basis-contract">
        <span className="font-medium">הכרזת ממשק הספק:</span>{" "}
        {contractRecorded ? (
          <>
            {contractId}
            {contractRevision ? `${contractId ? " · " : ""}מהדורה ${contractRevision}` : " · מהדורה לא נרשמה"}
          </>
        ) : (
          "לא נרשמה"
        )}
      </p>
      <p className="text-[11px] text-gray-500">
        "לא נרשמה" פירושו שההכרעה אינה נוקבת בבסיס הזה — לא שלא היה כזה. הנתונים הם מהתשובה של השרת
        ואינם משפיעים על שאלת החסימה.
      </p>
    </div>
  );
}

/**
 * One row of the trail. Every line is descriptive and none of them participates in the gate state.
 */
function TrailEntry({ entry, position }) {
  const { basis } = entry;
  const contractRecorded = !!(basis.contractId || basis.contractRevision);
  const contextEntries = Object.entries(entry.observedContext || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  return (
    <li className="rounded border border-gray-200 bg-white/70 p-2 space-y-0.5" data-testid="kg-trail-entry">
      <p className="text-xs font-medium text-gray-800">
        {/* The server's stored position. A row the server sent no position for is numbered by its
            place in the response and says so, rather than being given a position it never had. */}
        <span data-testid="kg-trail-entry-position">
          {entry.seq === null ? `הכרעה בסדר ${position} (מקום לא נרשם)` : `הכרעה #${entry.seq}`}
        </span>
        <span className="font-normal text-gray-600"> · {entry.recordedAt || "מועד לא נרשם"}</span>
        {" · "}
        <span data-testid="kg-trail-entry-decision">
          {entry.decision ? DECISION_LABEL[entry.decision] || entry.decision : "הכרעה שלא ניתן לקרוא"}
        </span>
      </p>
      <p className="text-xs text-gray-700">
        <span className="font-medium">סיבה:</span> {gateReasonLabel(entry.reasonCode)}
      </p>
      <TriggerList triggers={entry.triggers} testId="kg-trail-triggers" />
      {entry.researchAuthority && (
        <p className="text-xs text-gray-700">
          <span className="font-medium">מקור:</span>{" "}
          {researchAuthorityMeta(entry.researchAuthority).label}
          {entry.evidenceRef ? ` — ${entry.evidenceRef}` : ""}
        </p>
      )}
      {entry.unknownTriggers.length > 0 && (
        <p className="text-[11px] text-gray-600">
          טריגרים שאינם מוכרים לגרסת הממשק הזו (מוצגים כפי שנרשמו): {entry.unknownTriggers.join(", ")}
        </p>
      )}
      {contextEntries.length > 0 && (
        <p className="text-xs text-gray-600" data-testid="kg-trail-context">
          <span className="font-medium">ההקשר שנרשם:</span>{" "}
          {contextEntries.map(([k, v]) => `${k}=${v}`).join(", ")}
        </p>
      )}
      {/* The same all-or-nothing pack rule and the same full digest as the newest decision's basis:
          a partial triple is reported as no revision, and a shortened hash is a longer way of writing
          the revision number while looking like the identity. */}
      <p className="text-xs text-gray-700" data-testid="kg-trail-entry-basis">
        <span className="font-medium">בסיס:</span>{" "}
        {basis.pack
          ? `${basis.pack.scopeKey} · מהדורה ${basis.pack.revisionSeq} · ${basis.pack.contentHash}`
          : "חבילת מומחה: לא נרשמה"}
        {" · "}
        {contractRecorded
          ? `הכרזת ספק: ${basis.contractId || "לא נרשמה"}${
              basis.contractRevision ? ` · מהדורה ${basis.contractRevision}` : ""
            }`
          : "הכרזת ספק: לא נרשמה"}
      </p>
    </li>
  );
}

/**
 * The run's whole append-only decision trail — which is not the same thing as its current decision.
 *
 * MASTER_FLOW §4.1 requires the exact evidence a production-changing decision rested on to stay
 * recoverable, and `knowledge_gate_decisions` is append-only for that reason: the earlier
 * `research_required` that a later authoritative lookup reconciled has to stay readable rather than
 * being overwritten by the `proceed` that replaced it. `GET /runs/{run_id}` answers only the newest
 * row, so before this block existed a technician reading a cleared run could see the clearance and
 * not what it had cleared, against which baseline, or on whose citation — the earlier decision was
 * stored, named in the audit trail, and on no screen.
 *
 * Two rules, both pinned:
 *   - **the block never decides the gate state.** That stays `permits_mutation` on the response of the
 *     state read alone. Everything here is descriptive, and the block says so in its own footnote.
 *   - **an unreadable trail is stated as unreadable, never as "there is no history".** The entire
 *     point of the block is to stop the newest decision from reading as the whole record; rendering a
 *     failed read as an empty one would restore exactly that, one level down. That is why the summary
 *     line — which is visible while the block is collapsed — carries the count and the failure.
 */
function DecisionTrail({ trail }) {
  const { status, entries } = trail;
  return (
    <details className="text-xs" data-testid="kg-trail" data-trail-status={status}>
      <summary className="cursor-pointer text-gray-700" data-testid="kg-trail-summary">
        {status === GATE_TRAIL_STATUS.LOADING && "היסטוריית השער: טוען…"}
        {status === GATE_TRAIL_STATUS.UNAVAILABLE && (
          <span data-testid="kg-trail-unavailable">
            היסטוריית השער: לא ניתן היה לקרוא את הרשומה. ייתכן שנרשמו הכרעות נוספות לריצה זו שאינן
            מוצגות כאן.
          </span>
        )}
        {status === GATE_TRAIL_STATUS.READY &&
          (entries.length === 0
            ? "היסטוריית השער: השרת לא החזיר הכרעות."
            : entries.length === 1
            ? "היסטוריית השער: הכרעה אחת רשומה — זו הרשומה כולה."
            : `היסטוריית השער: ${entries.length} הכרעות, לפי סדר רישומן בשרת.`)}
      </summary>
      {status === GATE_TRAIL_STATUS.READY && entries.length > 0 && (
        <>
          <ol className="mt-1 space-y-1" data-testid="kg-trail-entries">
            {entries.map((entry, index) => (
              <TrailEntry key={entry.seq === null ? index : entry.seq} entry={entry} position={index + 1} />
            ))}
          </ol>
          <p className="mt-1 text-[11px] text-gray-500">
            הרשומה כאן היא תיעוד. מצב החסימה נקבע לפי תשובת קריאת המצב בלבד, ואינו נגזר מהרשומה הזו.
          </p>
        </>
      )}
    </details>
  );
}

function ObserveForm({ runId, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [triggers, setTriggers] = useState([]);
  const [context, setContext] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) =>
    setTriggers((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]));

  const submit = async (event) => {
    event.preventDefault();
    // The backend requires at least one trigger for the same reason: an empty observation evaluates
    // to PROCEED and would become the run's newest decision, unblocking it instead of restricting it.
    if (!triggers.length) {
      setError("יש לבחור לפחות אי-התאמה אחת. דיווח ריק אינו מחסום — הוא היה מסיר חסימה קיימת.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { triggers };
      const cleaned = Object.fromEntries(Object.entries(context).filter(([, v]) => (v || "").trim() !== ""));
      if (Object.keys(cleaned).length) payload.context = cleaned;
      onRecorded(await knowledgeGateApi.observe(runId, payload));
      setTriggers([]);
      setContext({});
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "שגיאה ברישום אי-ההתאמה");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-testid="kg-observe-open"
        onClick={() => setOpen(true)}
        className="border border-gray-300 hover:bg-gray-50 text-gray-700 px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50"
      >
        דווח על אי-התאמה שנצפתה
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded border border-gray-300 bg-white p-3" data-testid="kg-observe-form">
      <p className="text-xs text-gray-700">
        מה נצפה בפועל ולא תאם את מה שחבילת המומחה המאושרת מתארת? הדיווח מפעיל את מסלול אי-ההתאמה:
        הוא <span className="font-medium">עוצר</span> את הפעולה המאושרת עד שיירשם מחקר ממוקד במקור
        הרשמי של הספק. אין להשתמש בו כדי לעקוף בדיקה קיימת.
      </p>
      <fieldset className="space-y-1">
        <legend className="text-xs font-medium text-gray-700">סוגי אי-ההתאמה</legend>
        {GATE_TRIGGERS.map((t) => (
          <label key={t.id} className="flex items-start gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={triggers.includes(t.id)}
              onChange={() => toggle(t.id)}
              aria-label={t.label}
            />
            <span>
              <span className="font-medium">{t.label}</span>
              <span className="text-gray-500"> — {t.explain}</span>
            </span>
          </label>
        ))}
      </fieldset>
      <details className="text-xs">
        <summary className="cursor-pointer text-gray-600">הקשר שנצפה (רשות)</summary>
        <div className="mt-1 grid grid-cols-1 gap-1 sm:grid-cols-2">
          {OBSERVATION_CONTEXT_FIELDS.map((f) => (
            <label key={f.key} className="block text-xs text-gray-600">
              {f.label}
              <input
                type="text"
                value={context[f.key] || ""}
                placeholder={f.placeholder}
                onChange={(e) => setContext((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs"
              />
            </label>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-gray-500">
          נרשמים רק השדות האלה. אין להדביק כאן גוף תגובה של הספק או נתוני לקוח.
        </p>
      </details>
      {error && <p className="text-xs text-red-600" data-testid="kg-observe-error">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={busy}
          className="bg-red-600 hover:bg-red-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50"
        >
          {busy ? "..." : "רשום אי-התאמה"}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setError(null); }}
          disabled={busy}
          className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50"
        >
          ביטול
        </button>
      </div>
    </form>
  );
}

function ResearchForm({ runId, onRecorded }) {
  const [authority, setAuthority] = useState("");
  const [sourceRef, setSourceRef] = useState("");
  const [product, setProduct] = useState("");
  const [version, setVersion] = useState("");
  const [reconciles, setReconciles] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (!authority || !sourceRef.trim()) {
      setError("יש לבחור מקור ולצטט את המסמך או הרשומה שמהם נלקח המידע.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { source_authority: authority, source_ref: sourceRef.trim(), reconciles };
      if (product.trim()) payload.product = product.trim();
      if (version.trim()) payload.version = version.trim();
      onRecorded(await knowledgeGateApi.research(runId, payload));
      setSourceRef("");
      setReconciles(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "שגיאה ברישום המחקר");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2 rounded border border-violet-300 bg-white p-3" data-testid="kg-research-form">
      <p className="text-xs text-gray-700">
        מחקר ממוקד: איזה תיעוד <span className="font-medium">עדכני ורשמי של הספק</span> מיישב את
        אי-ההתאמה שנצפתה? רק מקור רשמי של הספק, עם ציטוט ומועד בדיקה, יכול להסיר את החסימה. רישום
        ראיות שאינן מיישבות את אי-ההתאמה לא יסיר את החסימה — השער יישאר חסום והפעולה לא תבוצע.
      </p>
      <label className="block text-xs text-gray-600">
        מקור
        <select
          value={authority}
          onChange={(e) => setAuthority(e.target.value)}
          aria-label="מקור המחקר"
          className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs"
        >
          <option value="">— בחירת מקור —</option>
          {RESEARCH_AUTHORITIES.map((a) => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>
      </label>
      {authority && (
        <p className="text-[11px] text-gray-500" data-testid="kg-authority-note">
          {researchAuthorityMeta(authority).explain}
        </p>
      )}
      <label className="block text-xs text-gray-600">
        ציטוט — המסמך או הרשומה שמהם נלקח המידע
        <input
          type="text"
          value={sourceRef}
          onChange={(e) => setSourceRef(e.target.value)}
          aria-label="ציטוט המקור"
          className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs"
        />
      </label>
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
        <label className="block text-xs text-gray-600">
          מוצר
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </label>
        <label className="block text-xs text-gray-600">
          גרסה
          <input
            type="text"
            value={version}
            onChange={(e) => setVersion(e.target.value)}
            className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-xs"
          />
        </label>
      </div>
      <label className="flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" checked={reconciles} onChange={(e) => setReconciles(e.target.checked)} />
        הראיות מיישבות את אי-ההתאמה שנצפתה
      </label>
      <p className="text-[11px] text-gray-500">
        מועד הבדיקה נרשם על ידי השרת בעת הרישום ואינו ניתן לעריכה מהממשק.
      </p>
      {error && <p className="text-xs text-red-600" data-testid="kg-research-error">{error}</p>}
      <button
        type="submit"
        disabled={busy}
        className="bg-violet-600 hover:bg-violet-700 text-white px-2.5 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50"
      >
        {busy ? "..." : "רשום מחקר והערך מחדש"}
      </button>
    </form>
  );
}

export default function KnowledgeGatePanel({ runId, className = "" }) {
  const queryClient = useQueryClient();
  const { can } = useAgentPermissions();
  const canObserve = can("agents_run");
  const canResearch = can("agents_approve");

  const query = useQuery({
    queryKey: knowledgeGateQueryKey(runId),
    queryFn: () => knowledgeGateApi.state(runId),
    enabled: !!runId,
    // A 404 is this endpoint's normal answer for "no mismatch was ever recorded", not a transient
    // failure, and retrying an unreadable state three times only delays telling the technician so.
    retry: false,
  });

  const classification = classifyGateQuery(query);
  const nextStep = gateNextStep(classification, { canObserve, canResearch });

  // Asked for only where the answer can mean something. The endpoint 404s when the run has no
  // recorded decision — which is the same fact the state read has already answered 404 for — so on
  // the normal path the request would be a guaranteed miss, and the panel already says there is no
  // decision. It is issued in the two states that show one.
  const hasDecision =
    classification.state === GATE_STATE.BLOCKED || classification.state === GATE_STATE.CLEARED;
  const historyQuery = useQuery({
    queryKey: knowledgeGateHistoryQueryKey(runId),
    queryFn: () => knowledgeGateApi.history(runId),
    enabled: !!runId && hasDecision,
    retry: false,
  });

  // The response body of a write IS the run's new decision, so the panel re-renders from the
  // server's own answer rather than from anything this component concluded.
  const onRecorded = (decision) => {
    if (decision && typeof decision === "object") {
      queryClient.setQueryData(knowledgeGateQueryKey(runId), decision);
    } else {
      queryClient.invalidateQueries({ queryKey: knowledgeGateQueryKey(runId) });
    }
    // Every write appends a row to the trail, so the trail can never be answered from the cache it
    // had before. `setQueryData` above is exact-match and does not reach this key.
    queryClient.invalidateQueries({ queryKey: knowledgeGateHistoryQueryKey(runId) });
  };

  if (!runId) return null;

  const { state, stale, malformed, errorDetail, errorStatus } = classification;

  return (
    <section
      dir="rtl"
      data-testid="knowledge-gate-panel"
      data-gate-state={state}
      aria-label="שער הידע המסתגל"
      className={`rounded border p-3 space-y-2 ${STATE_STYLE[state] || STATE_STYLE[GATE_STATE.LOADING]} ${className}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-xs font-bold text-gray-800">שער הידע המסתגל</h4>
        {classification.decision && (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full border border-gray-300 bg-white text-gray-700">
            {DECISION_LABEL[classification.decision] || classification.decision}
          </span>
        )}
        {stale && (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800" data-testid="kg-stale">
            הנתון אינו מעודכן — הרענון האחרון נכשל
          </span>
        )}
      </div>

      {state === GATE_STATE.LOADING && <p className="text-xs text-gray-500">טוען את מצב השער…</p>}

      {state === GATE_STATE.NORMAL_PATH && (
        <p className="text-xs text-gray-700" data-testid="kg-normal-path">
          לא נרשמה אי-התאמה לריצה זו — מסלול רגיל. השער אינו חוסם את הפעולה. אין לקרוא מכאן
          אישור: ביצוע הפעולה כפוף לשאר הבדיקות (מצב חירום, הרשאות, מצב הקריאה).
        </p>
      )}

      {state === GATE_STATE.BLOCKED && (
        <div className="space-y-2" data-testid="kg-blocked">
          <p className="text-xs font-medium text-red-800">
            הפעולה המאושרת אינה מבוצעת. זוהתה אי-התאמה מול חבילת המומחה המאושרת והיא טרם יושבה
            במקור הרשמי של הספק. אין לנחש — יש לאמת מול התיעוד העדכני.
          </p>
          <p className="text-xs text-gray-700">
            <span className="font-medium">סיבת החסימה:</span> {gateReasonLabel(classification.reasonCode)}
          </p>
          <TriggerList triggers={classification.triggers} />
          <RecordedContext context={classification.observedContext} />
          <DecisionBasis basis={classification.basis} />
          {nextStep === "escalate_to_approver" && (
            <p className="text-xs text-amber-800" data-testid="kg-needs-approver">
              רישום המחקר שמסיר את החסימה דורש הרשאת אישור פעולות (agents_approve), שאין לך.
              יש להעביר את ממצאי המחקר למי שמאשר את הפעולה.
            </p>
          )}
        </div>
      )}

      {state === GATE_STATE.CLEARED && (
        <div className="space-y-2" data-testid="kg-cleared">
          <p className="text-xs text-gray-700">
            אי-ההתאמה יושבה בראיות ספק עדכניות, ולכן השער אינו חוסם עוד את הפעולה המאושרת.
          </p>
          <p className="text-xs text-gray-700">
            <span className="font-medium">סיבה:</span> {gateReasonLabel(classification.reasonCode)}
          </p>
          {classification.researchAuthority && (
            <p className="text-xs text-gray-700">
              <span className="font-medium">מקור:</span>{" "}
              {researchAuthorityMeta(classification.researchAuthority).label}
              {classification.evidenceRef ? ` — ${classification.evidenceRef}` : ""}
            </p>
          )}
          <DecisionBasis basis={classification.basis} />
          <p className="text-[11px] text-gray-500">
            יישוב אי-התאמה אינו מאשר את הפעולה מחדש ואינו מעניק הרשאה: הפעולה כבר אושרה על ידי אדם,
            וכעת ניתן להריץ אותה מחדש.
          </p>
        </div>
      )}

      {hasDecision && <DecisionTrail trail={classifyGateTrailQuery(historyQuery)} />}

      {state === GATE_STATE.UNKNOWN && (
        <div className="space-y-1" data-testid="kg-unknown">
          <p className="text-xs text-amber-900">
            לא ניתן לאמת את מצב שער הידע לריצה זו. המערכת חוסמת פעולה במצב זה — אין להסיק מכאן
            שהשער פתוח.
          </p>
          {errorStatus != null && (
            <p className="text-[11px] text-gray-600">קוד התשובה מהשרת: {errorStatus}</p>
          )}
          {errorDetail && <p className="text-[11px] text-gray-600">{errorDetail}</p>}
          {malformed && (
            <p className="text-[11px] text-gray-600" data-testid="kg-malformed">
              התשובה מהשרת אינה נושאת הכרעה תקינה בשאלת החסימה.
            </p>
          )}
        </div>
      )}

      {classification.unknownTriggers?.length > 0 && (
        <p className="text-[11px] text-gray-600" data-testid="kg-unknown-triggers">
          טריגרים שנרשמו בשרת ואינם מוכרים לגרסת הממשק הזו (מוצגים כפי שנרשמו):{" "}
          {classification.unknownTriggers.join(", ")}
        </p>
      )}

      {canObserve && state !== GATE_STATE.UNKNOWN && (
        <ObserveForm runId={runId} onRecorded={onRecorded} />
      )}

      {state === GATE_STATE.BLOCKED && canResearch && <ResearchForm runId={runId} onRecorded={onRecorded} />}

      <details className="text-[11px] text-gray-500">
        <summary className="cursor-pointer">מה זה שער הידע המסתגל?</summary>
        <p className="mt-1">
          כשהעובד הדיגיטלי נתקל במשהו שאינו תואם את מה שחבילת המומחה המאושרת מתארת — שדה API שהשתנה,
          מסך שונה, הודעת הפסקת תמיכה, התנהגות הרשאות לא צפויה, אי-התאמת גרסה, ראיות סותרות או רמת
          ביטחון נמוכה — הוא עוצר <span className="font-medium">לפני</span> הפעולה שמשנה מצב, ואינו
          מנחש. הוא מחפש את התשובה בתיעוד העדכני והרשמי של הספק; אם התשובה לא נמצאה, הפעולה נחסמת
          ומוסלמת עם הראיות החסרות.
        </p>
      </details>
    </section>
  );
}
